import { Component, effect, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { TreeSignal } from '@xanadu/signal';
import { Genus, ContentModel, ViewAttributes, Folder, Aspect } from '@xanadu/model';
import { SecureComponent } from './secure.component';
import { AspectCache } from '@xanadu/cache';
import { InputConstant } from '@xanadu/input';
import { FormGroup } from '@angular/forms';

@Component({
    selector: 'attribute',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class AttributeComponent<T extends Genus> extends SecureComponent<T> {
    private readonly aspectCache = inject(AspectCache);

    protected readonly treeSignal = inject(TreeSignal);

    attributes = signal<Aspect[] | undefined>(undefined);

    private viewAttributes?: ViewAttributes;

    contentModel =  signal<ContentModel | undefined>(undefined);

    formData: FormGroup = new FormGroup({});

    constructor() {
        super();

        effect(() => {
            const model = this.model();
            const genus = this.node();
            const aspects = this.aspectCache.signal();

            if (genus && aspects) {
                this.viewAttributes = new ViewAttributes(model.custome, aspects);
                this.viewAttributes.load(genus.attributes);

                if (genus instanceof Folder && genus.inheritAttributes?.length) {
                    this.viewAttributes.load(genus.inheritAttributes);
                }

                this.contentModel.set(this.viewAttributes.contentModel(genus));
            }
        });

        effect(() => {
            const formGroup = this.formGroup();

            if (formGroup) {
                formGroup.addControl('data', this.formData);
            }
        });

        effect(() => {
            const attributes = this.attributes();
            const genus = this.node();

            if (attributes && this.viewAttributes && genus) {
                 this.viewAttributes.reload(attributes);

                if (genus instanceof Folder && genus.inheritAttributes?.length) {
                    this.viewAttributes.load(genus.inheritAttributes);
                }

                this.contentModel.set(this.viewAttributes.contentModel(genus));
            }
        });
    }

    protected override setup(genus: T): void {
        super.setup(genus);

        this.formData.patchValue(genus.data);
    }

    protected override load(genus: T, form: any): string | undefined {
        super.load(genus, form);

        genus.data = this.formData.value;
        Object.keys(genus.data).forEach(key => {
            if (key.startsWith(InputConstant.ignorePrefix)) {
                delete genus.data[key];
            }
        });
        return;
    }

    onChange(aspects: Aspect[]): void {
        this.attributes.set([...aspects]);
    }
}
